import { Card, CardContent } from '@/components/ui/card';
import { HorizonScanningItem } from '@/data/horizonScanningTypes';
import { PriorityBadge } from './PriorityBadge';
import { AddToActionPlanButton } from './AddToActionPlanButton';
import { Badge } from '@/components/ui/badge';
import { FileText, AlertTriangle, CheckCircle, Wrench } from 'lucide-react';
import { cn } from '@/lib/utils';

interface HorizonScanningGridProps {
  items: HorizonScanningItem[];
}

export function HorizonScanningGrid({ items }: HorizonScanningGridProps) {
  const getDomainIcon = (domain: string) => {
    switch (domain) {
      case 'Safety':
        return <AlertTriangle className="h-4 w-4" />;
      case 'Operations':
        return <Wrench className="h-4 w-4" />;
      case 'Environment':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-8">
      {/* Column Headers */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
        <div className="text-center">
          <h3 className="font-bold text-lg mb-2">Amendment Notes & Context</h3>
          <p className="text-sm text-muted-foreground">Regulation details and rationale</p>
        </div>
        <div className="text-center">
          <h3 className="font-bold text-lg mb-2">Regulatory Impact (EASA/National)</h3>
          <p className="text-sm text-muted-foreground">Impact assessment and alignment</p>
        </div>
        <div className="text-center">
          <h3 className="font-bold text-lg mb-2">Recommendations & Priority Score</h3>
          <p className="text-sm text-muted-foreground">Action items and priority assessment</p>
        </div>
      </div>

      {/* Grid Items */}
      <div className="space-y-6">
        {items.map((item, index) => (
          <div 
            key={item.id} 
            className={cn(
              'grid grid-cols-1 md:grid-cols-3 gap-6',
              index < items.length - 1 && 'pb-6 border-b'
            )}
          >
            {/* Column 1: Amendment Notes & Context */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-bold text-base">{item.section}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <FileText className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{item.source}</span>
                    </div>
                  </div>
                  <Badge variant="outline" className="flex items-center gap-1">
                    {getDomainIcon(item.domain)}
                    {item.domain}
                  </Badge>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Excerpt:</p>
                    <p className="text-sm">{item.excerpt}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Rationale:</p>
                    <p className="text-sm text-muted-foreground">{item.rationale}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Column 2: Regulatory Impact */}
            <Card>
              <CardContent className="pt-6">
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-4 w-4 text-blue-600" />
                    <span className="font-medium">{item.regulatoryImpact.file}</span>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    Section: {item.regulatoryImpact.section}
                  </Badge>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Impact Assessment:</p>
                    <p className="text-sm">{item.regulatoryImpact.assessment}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Excerpt Match:</p>
                    <p className="text-sm text-muted-foreground">{item.regulatoryImpact.excerptMatch}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Column 3: Recommendations & Priority Score */}
            <Card>
              <CardContent className="pt-6">
                <div className="mb-4">
                  <PriorityBadge 
                    score={item.recommendations.priorityScore} 
                    level={item.recommendations.priorityLevel} 
                  />
                </div>
                
                <div className="space-y-3 mb-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Recommendations:</p>
                    <ul className="text-sm space-y-1">
                      {item.recommendations.recommendation.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-muted-foreground mt-1">•</span>
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">Action:</p>
                    <p className="text-sm font-medium">{item.recommendations.action}</p>
                  </div>
                </div>
                
                <AddToActionPlanButton 
                  section={item.section}
                  className="w-full"
                />
              </CardContent>
            </Card>
          </div>
        ))}
      </div>
    </div>
  );
}